/*
 *   Copyright (c) 2026, CYBRA Corp. All rights reserved. 
 */
import { Injectable } from "@angular/core";
import { ServiceService } from "./service.service";
import { PropertyPatch } from "app/models/property-patch";

/*
 * Constants pertaining to the "AbleNet Shipment Validation" Service
 */
/** UUID for the "AbleNet Shipment Validation" Service Type */
export const AbleNetShipmentValidation_Type_Uuid = '00880915-4f33-4d7f-b96f-dab9897ea390';
/** Get Current State */
const FunctionKey_GetCurrentState = "get_state";
/** Function : Enter Shipment Nbr */
const FunctionKey_EnterShipmentNbr = "enter_shipment_nbr";
const FunctionParameterKey_ShipmentNbr = "shipment_nbr";
/** Function Process Shipment */
const FunctionKey_ProcessShipment = "process_shipment";
const FunctionParameterKey_EPC = "epc";
const FunctionParameterKey_Override = "override";
const FunctionParameterKey_IsAWarranty = "is_a_warranty";
/** Function Clear All / Cancel */
const FunctionKey_ClearAll = "clear_all";
/** Function Clear Reads */
const FunctionKey_ClearReads = "clear_reads";


/**
 * Service which provides functions used by the "AbleNets Shipment Validation" process.
 * 
 * This calls Functions on "Ablenet's Validation" Service instances in Edgefinity and
 * returns the results.
 */
@Injectable()
export class AblenetService {

  /** DTO to store the Shipment ID & Package ID after validation */
  shipmentValidationInfo: AbleNetShipmentValidation_AttemptDto = null;

  constructor(
      private serviceService: ServiceService
  ) { }

    /**
     * Calls the "Get Current State" Function on the "AbleNet Shipment Validation" Service with the given ID
     * and returns the result.
     */
    public getCurrentState(portalId: number | string): Promise<ValidationInfoDto> {
      return this.serviceService.executeFunctionByKey(portalId, FunctionKey_GetCurrentState, null);
    }

    // Validate the Shipment Nbr & retrive the Shipment Info - Shipment ID & Package ID
    public async validateShipment(portalId: number | string,shipmentNbr: string) : Promise<ValidationInfoDto> {
      const functionData: PropertyPatch[] = [];
      functionData.push({ property: FunctionParameterKey_ShipmentNbr, value: shipmentNbr} as PropertyPatch); 
      return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_EnterShipmentNbr, functionData);
    }  

      /**
       * Calls the "Clear Reads" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public clearReads(portalId: number | string): Promise<ValidationInfoDto> {
        return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearReads, null);
      }

      /**
       * Calls the "Clear All/Cancel" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public clearAll(portalId: number | string): Promise<ValidationInfoDto> {
        return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearAll, null);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public async processShipment(portalId: number | string, shipmentId: string, packageId: string, epc: string, override: boolean, isAWarranty: boolean): Promise<ValidationInfoDto> {
        const functionData: PropertyPatch[] = [];
        functionData.push({ property: FunctionParameterKey_EPC, value: epc} as PropertyPatch); 
        functionData.push({ property: FunctionParameterKey_Override, value: override.toString()} as PropertyPatch);
        functionData.push({ property: FunctionParameterKey_IsAWarranty, value: isAWarranty.toString() } as PropertyPatch);
        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_ProcessShipment, functionData);
      }

}

/**
 * Enum representing the possible "State" values for the Shipment Validation Process
 * being performed by the "AbleNet Shipment Validation" Services in Edgefinity.
 */
export enum AbleNetShipmentValidation_ValidationStateEnum {
  /**
   * Shipment Information needs to be entered for the Shipment / Items
   * to be validated.
   */
  EnterShipmentNbr = 'EnterShipmentNbr',

  /**
   * Shipment validtion in progress..takes sometime to send request to Acumatica & get response back
   */
  ShipmentValidationInProgress = 'ShipmentValidationInProgress',

}

/**
 * DTO to store the Asset details
 */
export interface AbleNetValidation_AssetDetailDto {
  /** EPC value of the Tag read */
  epc: string;
  assetId: string;
}

/**
 * DTO to handle the data being returned from Acumatica after Validation Attempt
 */
export interface AbleNetShipmentValidation_InfoDto {
  shipmentId: string;
  packageId: string | null;
  fnNumber: string | null;
  rfidNumber: string | null;
}

/**
 * DTO representing an Shipment Validation Attempt being performed by the 
 * "AbleNet Shipment Validation" Services
 */
export interface AbleNetShipmentValidation_AttemptDto {
  /** Number of the Shipment being processed against */
  shipmentNbr: string;
  shipmentValidationInfo: AbleNetShipmentValidation_InfoDto;
  overrideRoles: string[];
}

/**
 * DTO representing the "State" the "Shipment Validation Portal" Services
 * are currently in.  This also contains the information on any 
 * Validation Attempt being performed by the Service. 
 */
export interface ValidationInfoDto {
  state: AbleNetShipmentValidation_ValidationStateEnum;
  readerStatus: AbleNetReaderStateEnum | null;
  attemptDto: AbleNetShipmentValidation_AttemptDto | null;
  asset: AbleNetValidation_AssetDetailDto | null;
}

/**
 * Reader Status
 */
export enum AbleNetReaderStateEnum {
    Disconnected = 'Disconnected',
    Stopped = 'Stopped',
    Started = 'Started',
    Reconnecting = 'Reconnecting...',
    Restarting = 'Restarting...'
}

